/**
 * LandingPage - Homepage pubblica per Il Condominio Facile
 * Features:
 * - Hero section con login
 * - Sezione CO.DI.S.
 * - Sezione Risorse (articoli e documenti)
 * - Sezione Contatti
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Building2, ExternalLink, KeyRound, Mail, ArrowLeft, Loader2,
  ChevronDown, ChevronRight, FileText, Link as LinkIcon, Phone, 
  MapPin, Shield, Eye, Users, Clock, CheckCircle2, BookOpen,
  Briefcase, X, Quote, ChevronLeft, Lightbulb, Heart, Star, 
  MessageCircle, ThumbsUp, Award, Sparkles, Target
} from "lucide-react";

// Icone per le storie (ruotano in base all'indice)
const STORIE_ICONS = [
  { icon: MessageCircle, bgColor: 'bg-amber-100', iconColor: 'text-amber-600' },
  { icon: Lightbulb, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' },
  { icon: Heart, bgColor: 'bg-rose-100', iconColor: 'text-rose-600' },
  { icon: Star, bgColor: 'bg-purple-100', iconColor: 'text-purple-600' },
  { icon: ThumbsUp, bgColor: 'bg-green-100', iconColor: 'text-green-600' },
  { icon: Award, bgColor: 'bg-cyan-100', iconColor: 'text-cyan-600' },
  { icon: Sparkles, bgColor: 'bg-pink-100', iconColor: 'text-pink-600' },
  { icon: Target, bgColor: 'bg-orange-100', iconColor: 'text-orange-600' },
];
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { BACKEND_URL } from "@/config/api";

const heroImages = [
  { url: "https://images.unsplash.com/photo-1759671953272-a4719ca32266?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Modern building" },
  { url: "https://images.unsplash.com/photo-1712763505655-1077b86cea59?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Apartment complex" },
  { url: "https://images.unsplash.com/photo-1657442729269-1270ac727656?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Tall building" },
  { url: "https://images.unsplash.com/photo-1764726201399-ca28bafb2fd7?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Apartments" }
];

const codisObjectives = [
  { icon: Eye, title: "Trasparenza economica", desc: "Favorire la trasparenza della gestione economica" },
  { icon: FileText, title: "Gestione documentale", desc: "Gestione organica e durevole della documentazione" },
  { icon: CheckCircle2, title: "Controllo semplificato", desc: "Semplificare l'attività di controllo dei Condòmini" },
  { icon: Shield, title: "Sicurezza e fiducia", desc: "Aumentare la confidenza verso l'Amministratore" },
  { icon: Users, title: "Rete affidabile di collaboratori", desc: "Utilizzo di collaboratori selezionati e di provata esperienza professionale" }
];

const contactInfo = {
  name: "Ing. Giovanni Marchignoli",
  credentials: [
    { text: "Ord. Ing. Roma nr. 17497" },
    { text: "ANACI nr. 19407" },
    { text: "Mediatore Civile e Commerciale nr. 1098 - ", link: { text: "Leone ADR", url: "https://www.leoneadr.it" } }
  ],
  phone: "+39 327 2736906",
  phoneNote: "solo WhatsApp",
  email: "il.condominio.facile@gmail.com",
  meetingMode: "Interazioni online su piattaforma Google Meet"
};

// Category labels for display
const defaultCategoryLabels = {
  "analisi_studi": "Analisi & Studi",
  "articoli": "Selezione Articoli"
};

export default function LandingPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loginMode, setLoginMode] = useState("choose");
  const [uid, setUid] = useState("");
  const [pin, setPin] = useState("");
  const [otp, setOtp] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpType, setOtpType] = useState("email"); // "preconfigured" or "email"
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Resources state
  const [risorse, setRisorse] = useState([]);
  const [loadingRisorse, setLoadingRisorse] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categoryLabels, setCategoryLabels] = useState(defaultCategoryLabels);
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  
  // Storie / Casi Studio state
  const [storie, setStorie] = useState([]);
  const [currentStoria, setCurrentStoria] = useState(0);
  
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Redirect if logged in - based on role
  useEffect(() => {
    if (!loading && user) {
      const userRole = user.role;
      if (userRole === "condomino") {
        navigate("/condomino");
      } else if (userRole === "fornitore") {
        navigate("/fornitore");
      } else {
        navigate("/admin");
      }
    }
  }, [user, loading, navigate]);

  // Fetch public resources
  const fetchRisorse = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/risorse-pubbliche`);
      setRisorse(response.data);
      
      // Build dynamic category labels from data
      const dynamicLabels = { ...defaultCategoryLabels };
      response.data.forEach(r => {
        if (r.categoria && !dynamicLabels[r.categoria]) {
          // Use categoria_label if present, otherwise format the value
          dynamicLabels[r.categoria] = r.categoria_label || 
            r.categoria.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
      });
      setCategoryLabels(dynamicLabels);
      
      // Auto-expand first category
      if (response.data.length > 0) {
        const firstCat = response.data[0].categoria;
        setExpandedCategories({ [firstCat]: true });
      }
    } catch (error) {
      console.error("Error fetching risorse:", error);
    } finally {
      setLoadingRisorse(false);
    }
  }, []);

  useEffect(() => {
    fetchRisorse();
  }, [fetchRisorse]);

  // Fetch storie pubbliche
  useEffect(() => {
    const fetchStorie = async () => {
      try {
        const response = await axios.get(`${API}/storie/pubbliche`);
        setStorie(response.data.storie || []);
      } catch (error) {
        console.error("Error fetching storie:", error);
      }
    };
    fetchStorie();
  }, []);

  // Auto-advance storie carousel (10 secondi per dare tempo di leggere)
  useEffect(() => {
    if (storie.length < 3) return;
    const timer = setInterval(() => {
      setCurrentStoria((prev) => (prev + 1) % storie.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [storie.length]);

  // Group resources by category and subcategory
  const groupedRisorse = risorse.reduce((acc, r) => {
    if (!acc[r.categoria]) acc[r.categoria] = {};
    if (!acc[r.categoria][r.sottocategoria]) acc[r.categoria][r.sottocategoria] = [];
    acc[r.categoria][r.sottocategoria].push(r);
    return acc;
  }, {});

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const toggleSubcategory = (cat, subcat) => {
    const key = `${cat}-${subcat}`;
    setExpandedSubcategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleUidPinSubmit = async (e) => {
    e.preventDefault();
    if (!uid.trim() || !pin.trim()) {
      toast.error("Inserisci UserID e PIN");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-uid`, { login_uid: uid, login_pin: pin });
      setMaskedEmail(response.data.email_masked);
      setOtpType(response.data.otp_type || "email");
      setLoginMode("otp");
      
      // Show appropriate message based on OTP type
      if (response.data.otp_type === "preconfigured") {
        toast.success("Inserisci il tuo codice OTP personale");
      } else {
        toast.success(response.data.email_sent 
          ? "Codice OTP inviato via email" 
          : "Inserisci il codice OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error("Inserisci il codice OTP");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { login_uid: uid, otp_code: otp }, { withCredentials: true });
      toast.success("Accesso effettuato!");
      // Use login function directly with response data instead of checkAuth
      login(response.data);
      
      // Navigate based on user role
      const userRole = response.data.role;
      if (userRole === "condomino") {
        navigate("/condomino");
      } else if (userRole === "fornitore") {
        navigate("/fornitore");
      } else {
        navigate("/admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice OTP non valido");
    } finally {
      setIsLoading(false);
    }
  };

  const resetLogin = () => {
    setLoginMode("choose");
    setUid("");
    setPin("");
    setOtp("");
    setMaskedEmail("");
    setOtpType("email");
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background Slider */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 1 }}
              animate={{ opacity: 1, scale: 1.1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1 }, scale: { duration: 10, ease: "easeOut" } }}
              className="absolute inset-0"
            >
              <img src={heroImages[currentSlide].url} alt={heroImages[currentSlide].alt} className="w-full h-full object-cover" />
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Header */}
          <header className="p-6 md:p-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                <Building2 className="w-8 h-8 text-slate-900" />
              </div>
              <span className="text-white font-semibold text-xl font-['Outfit']">Il Condominio Facile</span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              {storie.length >= 3 && (
                <button onClick={() => scrollToSection('storie')} className="text-white/80 hover:text-white transition-colors">Storie</button>
              )}
              <button onClick={() => scrollToSection('codis')} className="text-white/80 hover:text-white transition-colors">CO.DI.S.</button>
              <button onClick={() => scrollToSection('risorse')} className="text-white/80 hover:text-white transition-colors">Risorse</button>
              <button onClick={() => scrollToSection('contatti')} className="text-white/80 hover:text-white transition-colors">Contatti</button>
              <Button onClick={() => setShowLoginModal(true)} className="bg-white text-slate-900 hover:bg-white/90">
                Accedi
              </Button>
            </nav>
            <Button onClick={() => setShowLoginModal(true)} className="md:hidden bg-white text-slate-900 hover:bg-white/90">
              Accedi
            </Button>
          </header>

          {/* Hero Content */}
          <main className="flex-1 flex items-center justify-center px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-center max-w-3xl"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-['Outfit'] tracking-tight">
                Il Condominio Facile
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-4 font-light">
                Gestione e Recupero Condomini con metodologia <strong className="font-semibold">CO.DI.S.</strong>
              </p>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                <strong>CO</strong>ndominio <strong>DI</strong>gitale e <strong>S</strong>icuro: 
                trasparenza, condivisione e controllo semplificato per tutti i Condòmini.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => setShowLoginModal(true)} 
                  size="lg" 
                  className="bg-white text-slate-900 hover:bg-white/90 px-8 py-6 text-lg"
                >
                  Accedi alla piattaforma
                </Button>
                <Button 
                  onClick={() => scrollToSection('codis')} 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  Scopri di più
                </Button>
              </div>
              
              {/* Slide Indicators */}
              <div className="flex justify-center gap-2 mt-12">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentSlide ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`}
                  />
                ))}
              </div>
            </motion.div>
          </main>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </div>
        </div>
      </section>

      {/* ========== STORIE / CASI STUDIO SECTION ========== */}
      {storie.length >= 3 && (
        <section id="storie" className="py-20 px-6 md:px-12 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">
                Storie comuni, storie note, storie che si ripetono...
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Storie tratte da Condomini che ci hanno scelto
              </p>
            </motion.div>

            {/* Carousel */}
            <div className="relative">
              {/* Navigation arrows */}
              <button 
                onClick={() => setCurrentStoria(prev => prev === 0 ? storie.length - 1 : prev - 1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-slate-600" />
              </button>
              <button 
                onClick={() => setCurrentStoria(prev => (prev + 1) % storie.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-slate-50 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-slate-600" />
              </button>

              {/* Story cards */}
              <div className="overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStoria}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-3xl mx-auto"
                  >
                    {(() => {
                      const iconConfig = STORIE_ICONS[currentStoria % STORIE_ICONS.length];
                      const IconComponent = iconConfig.icon;
                      return (
                        <Card className="bg-white shadow-xl border-0">
                          <CardContent className="p-8 md:p-12">
                            <div className="flex items-start gap-4 mb-6">
                              <div className={`p-3 ${iconConfig.bgColor} rounded-full shrink-0`}>
                                <IconComponent className={`w-6 h-6 ${iconConfig.iconColor}`} />
                              </div>
                              <div>
                                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-1">
                                  {storie[currentStoria]?.titolo}
                                </h3>
                                {storie[currentStoria]?.sottotitolo && (
                                  <p className="text-amber-600 font-medium">
                                    {storie[currentStoria]?.sottotitolo}
                                  </p>
                                )}
                              </div>
                            </div>
                            <p className="text-slate-600 text-lg leading-relaxed">
                              {storie[currentStoria]?.testo}
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {storie.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStoria(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      idx === currentStoria 
                        ? 'bg-amber-500' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== CO.DI.S. SECTION ========== */}
      <section id="codis" className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">
              La metodologia CO.DI.S.
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Proponiamo una gestione del Condominio effettuata attraverso operatività prevalentemente 
              digitali per semplificare il controllo da parte dei Condomini e/o Consiglieri.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {codisObjectives.map((obj, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow border-slate-200">
                  <CardContent className="p-6">
                    <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <obj.icon className="w-6 h-6 text-slate-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{obj.title}</h3>
                    <p className="text-slate-600">{obj.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-slate-500 mt-12 text-sm"
          >
            Il nome <strong>CO.DI.S.</strong> ed il suo Logo sono proprietà esclusiva di Ingegneria ed Amministrazione.
          </motion.p>
        </div>
      </section>

      {/* ========== RISORSE SECTION ========== */}
      <section id="risorse" className="py-20 px-6 md:px-12 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">
              Risorse e Articoli
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Una selezione di documenti, analisi e articoli utili per la gestione condominiale.
            </p>
          </motion.div>

          {loadingRisorse ? (
            <div className="text-center py-12 text-slate-500">Caricamento risorse...</div>
          ) : Object.keys(groupedRisorse).length === 0 ? (
            <div className="text-center py-12 text-slate-500">Nessuna risorsa disponibile</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedRisorse).map(([categoria, sottocategorie]) => (
                <Card key={categoria} className="overflow-hidden">
                  <button
                    onClick={() => toggleCategory(categoria)}
                    className="w-full p-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-slate-900 w-10 h-10 rounded-lg flex items-center justify-center">
                        {categoria === "analisi_studi" ? (
                          <Briefcase className="w-5 h-5 text-white" />
                        ) : (
                          <BookOpen className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {categoryLabels[categoria] || categoria}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {Object.values(sottocategorie).flat().length} elementi
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedCategories[categoria] ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {expandedCategories[categoria] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-slate-100"
                      >
                        <div className="p-4 space-y-2">
                          {Object.entries(sottocategorie).map(([sottocategoria, items]) => {
                            const subKey = `${categoria}-${sottocategoria}`;
                            const isExpanded = expandedSubcategories[subKey];
                            
                            return (
                              <div key={sottocategoria} className="bg-slate-50 rounded-lg overflow-hidden">
                                <button
                                  onClick={() => toggleSubcategory(categoria, sottocategoria)}
                                  className="w-full p-4 flex items-center justify-between hover:bg-slate-100 transition-colors text-left"
                                >
                                  <span className="font-medium text-slate-700">{sottocategoria || "Generale"}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">
                                      {items.length}
                                    </span>
                                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  </div>
                                </button>
                                
                                <AnimatePresence>
                                  {isExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      className="border-t border-slate-200"
                                    >
                                      <ul className="p-4 space-y-2">
                                        {items.map((item) => {
                                          // Build URL - local files start with /api, external links are full URLs
                                          const itemUrl = item.url.startsWith('/api') 
                                            ? `${BACKEND_URL}${item.url}` 
                                            : item.url;
                                          
                                          return (
                                            <li key={item.risorsa_id}>
                                              <a
                                                href={itemUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors group"
                                              >
                                                {item.tipo === "pdf" ? (
                                                  <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                                                ) : (
                                                  <LinkIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                                )}
                                                <span className="text-slate-700 group-hover:text-slate-900 flex-1">
                                                  {item.titolo}
                                                </span>
                                                <ExternalLink className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                              </a>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== CONTATTI SECTION ========== */}
      <section id="contatti" className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">
              Contatti
            </h2>
          </motion.div>

          <Card className="overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Info */}
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-slate-900 w-16 h-16 rounded-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{contactInfo.name}</h3>
                      <p className="text-slate-500">Amministratore di Condominio</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                    {contactInfo.credentials.map((cred, i) => (
                      <p key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>
                          {cred.text}
                          {cred.link && (
                            <a 
                              href={cred.link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {cred.link.text}
                            </a>
                          )}
                        </span>
                      </p>
                    ))}
                  </div>
                </div>

                {/* Contact details */}
                <div className="space-y-4">
                  <a 
                    href={`https://wa.me/${contactInfo.phone.replace(/\s/g, '').replace('+', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-slate-900">{contactInfo.phone}</p>
                      <p className="text-sm text-slate-500">{contactInfo.phoneNote}</p>
                    </div>
                  </a>

                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center gap-4 p-4 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">{contactInfo.email}</p>
                      <p className="text-sm text-slate-500">Email principale</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 p-4 rounded-lg bg-purple-50">
                    <svg className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                    </svg>
                    <div>
                      <p className="font-medium text-slate-900">{contactInfo.meetingMode}</p>
                      <p className="text-sm text-slate-500">Meeting online su richiesta</p>
                    </div>
                  </div>

                  <a 
                    href={`mailto:${contactInfo.email}?subject=Richiesta%20contatto%20-%20Il%20Condominio%20Facile`}
                    className="block"
                  >
                    <Button className="w-full bg-slate-900 hover:bg-slate-800 py-6">
                      <Mail className="w-4 h-4 mr-2" />
                      Richiedi un contatto
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              <span className="font-semibold text-xl font-['Outfit']">Il Condominio Facile</span>
            </div>
            <div className="flex items-center gap-6">
              <button onClick={() => scrollToSection('codis')} className="text-white/70 hover:text-white transition-colors">CO.DI.S.</button>
              <button onClick={() => scrollToSection('risorse')} className="text-white/70 hover:text-white transition-colors">Risorse</button>
              <button onClick={() => scrollToSection('contatti')} className="text-white/70 hover:text-white transition-colors">Contatti</button>
              <Button onClick={() => setShowLoginModal(true)} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Accedi
              </Button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/50 text-sm">
            © {new Date().getFullYear()} Il Condominio Facile. Tutti i diritti riservati.
          </div>
        </div>
      </footer>

      {/* ========== LOGIN MODAL ========== */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => { setShowLoginModal(false); resetLogin(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
            >
              <button
                onClick={() => { setShowLoginModal(false); resetLogin(); }}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <AnimatePresence mode="wait">
                {loginMode === "choose" && (
                  <motion.div key="choose" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-slate-900 p-2 rounded-lg">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">Accedi</h2>
                    </div>
                    <p className="text-slate-600 mb-6">
                      Accedi alla piattaforma per gestire il tuo condominio.
                    </p>
                    
                    <div className="space-y-4">
                      {/* Google Login */}
                      <Button 
                        onClick={handleGoogleLogin}
                        className="w-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 py-6 text-lg" 
                        data-testid="login-google-btn"
                      >
                        <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Accedi con Google
                      </Button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-300" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">oppure</span></div>
                      </div>
                      
                      <Button onClick={() => setLoginMode("uid")} className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg" data-testid="login-uid-btn">
                        <KeyRound className="w-5 h-5 mr-3" />
                        Accedi con UserID e PIN
                      </Button>
                    </div>
                  </motion.div>
                )}

                {loginMode === "uid" && (
                  <motion.div key="uid" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <button onClick={resetLogin} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
                      <ArrowLeft className="w-4 h-4" />Torna indietro
                    </button>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Accedi con UserID e PIN</h2>
                    <p className="text-slate-600 mb-6">Inserisci le credenziali fornite dall'amministratore</p>
                    
                    <form onSubmit={handleUidPinSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="uid">UserID</Label>
                        <Input id="uid" value={uid} onChange={(e) => setUid(e.target.value)} placeholder="Il tuo UserID" data-testid="uid-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pin">PIN</Label>
                        <Input id="pin" type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Il tuo PIN" data-testid="pin-input" />
                      </div>
                      <Button type="submit" disabled={isLoading} className="w-full py-6 bg-slate-900 hover:bg-slate-800" data-testid="submit-uid-btn">
                        {isLoading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Verifica...</> : "Continua"}
                      </Button>
                    </form>
                  </motion.div>
                )}

                {loginMode === "otp" && (
                  <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <button onClick={() => setLoginMode("uid")} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
                      <ArrowLeft className="w-4 h-4" />Torna indietro
                    </button>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-3 rounded-full ${otpType === "preconfigured" ? "bg-green-100" : "bg-orange-100"}`}>
                        <KeyRound className={`w-6 h-6 ${otpType === "preconfigured" ? "text-green-600" : "text-orange-600"}`} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">Verifica OTP</h2>
                        {otpType === "preconfigured" ? (
                          <p className="text-slate-600 text-sm">Inserisci il tuo codice personale</p>
                        ) : (
                          <p className="text-slate-600 text-sm">Codice inviato a {maskedEmail}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-slate-600 mb-6">
                      {otpType === "preconfigured" 
                        ? "Inserisci il codice OTP che ti è stato fornito" 
                        : "Inserisci il codice OTP ricevuto via email"}
                    </p>
                    
                    <form onSubmit={handleOtpSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="otp">Codice OTP</Label>
                        <Input 
                          id="otp" 
                          value={otp} 
                          onChange={(e) => setOtp(otpType === "preconfigured" ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))} 
                          placeholder={otpType === "preconfigured" ? "Il tuo codice" : "000000"} 
                          className="text-center text-2xl tracking-widest font-mono" 
                          maxLength={otpType === "preconfigured" ? 20 : 6} 
                          data-testid="otp-input" 
                        />
                      </div>
                      <Button type="submit" disabled={isLoading || otp.length === 0} className="w-full py-6 bg-slate-900 hover:bg-slate-800" data-testid="submit-otp-btn">
                        {isLoading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Verifica...</> : "Accedi"}
                      </Button>
                    </form>
                    {otpType === "email" && (
                      <p className="text-center text-sm text-slate-500 mt-4">
                        Non hai ricevuto il codice?{" "}
                        <button onClick={handleUidPinSubmit} className="text-orange-600 hover:underline" disabled={isLoading}>Invia di nuovo</button>
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
